// API client for NexHR backend
const API_BASE =
  process.env.NEXT_PUBLIC_API_URL?.replace(/\/+$/, '') ||
  process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, '') ||
  '';

/** Set to false when the backend is available again. Skips all `fetch` calls below. */
export const API_CALLS_DISABLED = false;

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('nexhr_token') || '';
  }

  setToken(token) {
    this.token = token;
    if (token) localStorage.setItem('nexhr_token', token);
    else localStorage.removeItem('nexhr_token');
  }

  getToken() {
    return this.token || localStorage.getItem('nexhr_token') || '';
  }

  async request(path, options = {}) {
    if (API_CALLS_DISABLED) {
      // eslint-disable-next-line no-console -- intentional while server is offline
      console.warn(`[api] skipped (${options.method || 'GET'} ${path}) — API_CALLS_DISABLED`);
      throw new Error('API offline');
    }

    const url = `${API_BASE}${path}`;
    const isFormData = options?.body instanceof FormData;
    const headers = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    };
    const token = this.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(url, { ...options, headers });

    if (res.status === 401) {
      this.setToken('');
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    const responseBody = await res.json();
    if (!res.ok) {
      throw new Error(responseBody?.error || responseBody?.message || 'Request failed');
    }

    // Backend often responds as { success: true, data: ... }.
    // Unwrap once here so pages consistently receive the actual payload.
    // Preserve pagination metadata when present.
    if (responseBody && typeof responseBody === 'object' && 'data' in responseBody) {
      const hasPaginationMeta =
        responseBody.total !== undefined ||
        responseBody.page !== undefined ||
        responseBody.pages !== undefined;

      if (hasPaginationMeta) {
        return {
          items: responseBody.data,
          total: responseBody.total ?? 0,
          page: responseBody.page ?? 1,
          pages: responseBody.pages ?? 1,
          ...(responseBody.counts != null ? { counts: responseBody.counts } : {}),
        };
      }

      return responseBody.data;
    }

    return responseBody;
  }

  get(path) { return this.request(path); }
  post(path, body) { return this.request(path, { method: 'POST', body: JSON.stringify(body) }); }
  postForm(path, formData) { return this.request(path, { method: 'POST', body: formData }); }
  put(path, body) { return this.request(path, { method: 'PUT', body: JSON.stringify(body) }); }
  delete(path) { return this.request(path, { method: 'DELETE' }); }
}

export const api = new ApiClient();

const normalizeArrayPayload = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.employees)) return value.employees;
  return [];
};

const hasPaginationParams = (params) => {
  return params?.page !== undefined || params?.limit !== undefined;
};

// Auth
export const authApi = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  me: () => api.get('/api/auth/me'),
  logout: () => api.post('/api/auth/logout', {}),
};

// Employees
export const employeeApi = {
  getAll: async (params) => {
    const qs = params ? new URLSearchParams(params).toString() : '';
    const response = await api.get(`/api/employees${qs ? `?${qs}` : ''}`);
    if (hasPaginationParams(params)) return response;
    return normalizeArrayPayload(response);
  },
  /** Full employee list for exports — no page/limit; pages through if backend still paginates. */
  getAllForExport: async (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      )
    ).toString();
    const first = await api.get(`/api/employees${qs ? `?${qs}` : ''}`);
    if (Array.isArray(first)) return first;

    const firstItems = Array.isArray(first?.items)
      ? first.items
      : Array.isArray(first?.data)
        ? first.data
        : [];
    const totalPages = Number(first?.pages) || 1;
    const total = Number(first?.total) || firstItems.length;

    if (totalPages <= 1 && firstItems.length >= total) {
      return firstItems;
    }

    const all = [...firstItems];
    const pageSize = Math.max(firstItems.length, 1);
    for (let page = 2; page <= totalPages; page += 1) {
      const pageParams = new URLSearchParams({
        ...Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
        ),
        page: String(page),
        limit: String(pageSize),
      }).toString();
      const next = await api.get(`/api/employees?${pageParams}`);
      const batch = Array.isArray(next)
        ? next
        : Array.isArray(next?.items)
          ? next.items
          : Array.isArray(next?.data)
            ? next.data
            : [];
      if (batch.length === 0) break;
      all.push(...batch);
    }
    return all;
  },
  getById: (id) => api.get(`/api/employees/${id}`),
  create: (data) => api.post('/api/employees', data),
  update: (id, data) => api.put(`/api/employees/${id}`, data),
  remove: (id) => api.delete(`/api/employees/${id}`),
  getSalaryRevisions: (id) => api.get(`/api/employees/${id}/salary-revisions`),
  incrementSalary: (id, data) => api.post(`/api/employees/${id}/increment-salary`, data),
  getBirthdays: async () => {
    const response = await api.get('/api/employees/birthdays');
    const groups = response?.data && !Array.isArray(response.data) ? response.data : response;
    const asList = (value) => (Array.isArray(value) ? value : []);
    return {
      today: asList(groups?.today),
      tomorrow: asList(groups?.tomorrow),
      dayAfterTomorrow: asList(groups?.dayAfterTomorrow),
    };
  },
  getJoinedExited: ({ month, year, project, fromDate } = {}) => {
    const params = {};
    if (month) params.month = month;
    if (year != null && year !== '') params.year = String(year);
    if (project && project !== 'all') params.project = project;
    if (fromDate) params.fromDate = fromDate;
    const qs = new URLSearchParams(params).toString();
    return api.get(`/api/employees/joined-exited${qs ? `?${qs}` : ''}`);
  },
};

// Attendance
export const attendanceApi = {
  get: async (params) => {
    const qs = new URLSearchParams(params).toString();
    const response = await api.get(`/api/attendance?${qs}`);
    if (hasPaginationParams(params)) return response;
    return normalizeArrayPayload(response);
  },
  import: (records, month, year, company) => api.post('/api/attendance/import', { records, month, year, company }),
  importExcel: (file, year, month, company, project) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', year);
    formData.append('month', month);
    if (company) formData.append('company', company);
    if (project) formData.append('project', project);
    return api.postForm('/api/attendance/import-excel', formData);
  },
  importExcelPn: (file, year, month, company, project) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', year);
    formData.append('month', month);
    if (company) formData.append('company', company);
    if (project) formData.append('project', project);
    return api.postForm('/api/attendance/import-excel-pn', formData);
  },
  importExcelMn: (file, year, month, company, project) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', year);
    formData.append('month', month);
    if (company) formData.append('company', company);
    if (project) formData.append('project', project);
    return api.postForm('/api/attendance/import-excel-mn', formData);
  },
  validateLeaves: (month, year, employeeId) => {
    const params = { month, year };
    if (employeeId) params.employeeId = employeeId;
    const qs = new URLSearchParams(params).toString();
    return api.get(`/api/attendance/validate-leaves?${qs}`);
  },
  getStats: (month, year) => {
    const qs = new URLSearchParams({ month, year }).toString();
    return api.get(`/api/attendance/stats?${qs}`);
  },
  lock: (month, year) => api.post('/api/attendance/lock', { month, year }),
  export: (params = {}) => downloadBlob('/api/attendance/export', params),
};

const downloadBlob = async (path, params = {}) => {
  if (API_CALLS_DISABLED) {
    throw new Error('API offline');
  }

  const qs = new URLSearchParams(params).toString();
  const url = `${API_BASE}${path}${qs ? `?${qs}` : ''}`;
  const token = api.getToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (res.status === 401) {
    api.setToken('');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    let message = 'Export failed';
    try {
      const body = await res.json();
      message = body?.error || body?.message || message;
    } catch {
      // response may not be JSON
    }
    throw new Error(message);
  }

  return res;
};

// Leaves
export const leaveApi = {
  getAll: async (params) => {
    const qs = params ? new URLSearchParams(params).toString() : '';
    const response = await api.get(`/api/leaves${qs ? `?${qs}` : ''}`);
    if (hasPaginationParams(params)) return response;
    return normalizeArrayPayload(response);
  },
  /** Full leave list for exports — no page/limit; pages through if backend still paginates. */
  getAllForExport: async (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      )
    ).toString();
    const first = await api.get(`/api/leaves${qs ? `?${qs}` : ''}`);
    if (Array.isArray(first)) return first;

    const firstItems = Array.isArray(first?.items)
      ? first.items
      : Array.isArray(first?.data)
        ? first.data
        : Array.isArray(first?.leaves)
          ? first.leaves
          : [];
    const totalPages = Number(first?.pages) || 1;
    const total = Number(first?.total) || firstItems.length;

    if (totalPages <= 1 && firstItems.length >= total) {
      return firstItems;
    }

    const all = [...firstItems];
    const pageSize = Math.max(firstItems.length, 1);
    for (let page = 2; page <= totalPages; page += 1) {
      const pageParams = new URLSearchParams({
        ...Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
        ),
        page: String(page),
        limit: String(pageSize),
      }).toString();
      const next = await api.get(`/api/leaves?${pageParams}`);
      const batch = Array.isArray(next)
        ? next
        : Array.isArray(next?.items)
          ? next.items
          : Array.isArray(next?.data)
            ? next.data
            : Array.isArray(next?.leaves)
              ? next.leaves
              : [];
      if (batch.length === 0) break;
      all.push(...batch);
    }
    return all;
  },
  export: (params = {}) =>
    downloadBlob('/api/leaves', { ...params, export: 'true' }),
  apply: (data) => api.post('/api/leaves', data),
  review: (id, { status, reviewComment, type }) => {
    const body = { status, reviewComment };
    if (type) body.type = type;
    return api.put(`/api/leaves/${id}/review`, body);
  },
  cancel: (id, cancelComment) => {
    const body = cancelComment?.trim() ? { cancelComment: cancelComment.trim() } : {};
    return api.put(`/api/leaves/${id}/cancel`, body);
  },
  getBalance: (employeeId) => api.get(`/api/leaves/balances?employeeId=${employeeId || ''}`),
  getAllBalances: async () => normalizeArrayPayload(await api.get('/api/leaves/balances/all')),
};

// CompOff (compensatory off)
export const compoffApi = {
  getAll: async (params) => {
    const qs = params ? new URLSearchParams(params).toString() : '';
    const response = await api.get(`/api/compoffs${qs ? `?${qs}` : ''}`);
    if (hasPaginationParams(params)) return response;
    return normalizeArrayPayload(response);
  },
  apply: (data) => api.post('/api/compoffs', data),
  review: (id, { status, reviewComment }) => {
    const body = { status, reviewComment };
    return api.put(`/api/compoffs/${id}/review`, body);
  },
  cancel: (id, cancelComment) => {
    const body = cancelComment?.trim() ? { cancelComment: cancelComment.trim() } : {};
    return api.put(`/api/compoffs/${id}/cancel`, body);
  },
  getBalance: (employeeId) => {
    const qs = employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : '';
    return api.get(`/api/compoffs/balances${qs}`);
  },
};

// Holidays & Shifts
export const holidayApi = {
  getAll: async () => normalizeArrayPayload(await api.get('/api/holidays')),
  create: (data) => api.post('/api/holidays', data),
  update: (id, data) => api.put(`/api/holidays/${id}`, data),
  remove: (id) => api.delete(`/api/holidays/${id}`),
};

export const shiftApi = {
  getAll: async () => normalizeArrayPayload(await api.get('/api/shifts')),
  update: (id, data) => api.put(`/api/shifts/${id}`, data),
};

const payrollSortQuery = (params = {}) => {
  const sortBy = params?.sortBy;
  const sortOrder = params?.sortOrder === 'asc' || params?.sortOrder === 'desc'
    ? params.sortOrder
    : undefined;
  if (!sortBy && !sortOrder) return {};
  return {
    ...(sortBy ? { sortBy } : {}),
    ...(sortOrder ? { sortOrder } : {}),
  };
};

const toQueryString = (params = {}) =>
  new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    )
  ).toString();

// Payroll
export const payrollApi = {
  getAll: async (params) => {
    const qs = params ? new URLSearchParams(params).toString() : '';
    const response = await api.get(`/api/payroll${qs ? `?${qs}` : ''}`);
    if (hasPaginationParams(params)) return response;
    return normalizeArrayPayload(response);
  },
  getById: (id, params = {}) => {
    const qs = toQueryString(payrollSortQuery(params));
    return api.get(`/api/payroll/${id}${qs ? `?${qs}` : ''}`);
  },
  exportSheet: (id, params = {}) =>
    downloadBlob(`/api/payroll/${id}/export-sheet`, payrollSortQuery(params)),
  generate: (month, year, bonusDays, leaveAdjustments, advanceOverrides, byBank, other) =>
    api.post('/api/payroll/generate', {
      month,
      year,
      bonusDays,
      leaveAdjustments,
      advanceOverrides,
      byBank,
      other,
    }),
  freeze: (id) => api.put(`/api/payroll/${id}/freeze`, {}),
  process: (id) => api.put(`/api/payroll/${id}/process`, {}),
};

// Projects
export const projectApi = {
  getAll: async () => normalizeArrayPayload(await api.get('/api/projects')),
  getById: (id) => api.get(`/api/projects/${id}`),
  create: (data) => api.post('/api/projects', data),
  update: (id, data) => api.put(`/api/projects/${id}`, data),
  remove: (id) => api.delete(`/api/projects/${id}`),
};

// Companies
export const companyApi = {
  getAll: async () => normalizeArrayPayload(await api.get('/api/companies')),
  getById: (id) => api.get(`/api/companies/${id}`),
  create: (data) => api.post('/api/companies', data),
  update: (id, data) => api.put(`/api/companies/${id}`, data),
  remove: (id) => api.delete(`/api/companies/${id}`),
};

// Bonus
export const bonusApi = {
  getEmployees: async (params = {}) => {
    const qs = toQueryString(params);
    const response = await api.get(`/api/bonus/employees${qs ? `?${qs}` : ''}`);
    if (Array.isArray(response)) {
      return { items: response, total: response.length };
    }
    if (Array.isArray(response?.items)) {
      return {
        items: response.items,
        total: response.total ?? response.items.length,
      };
    }
    if (Array.isArray(response?.data)) {
      return {
        items: response.data,
        total: response.total ?? response.data.length,
      };
    }
    return { items: [], total: 0 };
  },
  preview: (data) => api.post('/api/bonus/preview', data),
  request: (data) => api.post('/api/bonus/request', data),
  getRequests: async (params = {}) => {
    const qs = toQueryString(params);
    const response = await api.get(`/api/bonus/requests${qs ? `?${qs}` : ''}`);
    if (Array.isArray(response)) {
      return {
        items: response,
        total: response.length,
        page: 1,
        pages: 1,
        counts: {},
      };
    }
    const items = Array.isArray(response?.items)
      ? response.items
      : Array.isArray(response?.data)
        ? response.data
        : [];
    return {
      items,
      total: response?.total ?? items.length,
      page: response?.page ?? 1,
      pages: response?.pages ?? 1,
      counts: response?.counts ?? {},
    };
  },
  approve: (id, data = {}) => api.put(`/api/bonus/requests/${id}/approve`, data),
  reject: (id, data = {}) => api.put(`/api/bonus/requests/${id}/reject`, data),
  bulkAction: (data) => api.post('/api/bonus/bulk-action', data),
  export: (params = {}) => downloadBlob('/api/bonus/export', params),
};

// Advances
export const advanceApi = {
  getAll: async (params) => {
    const qs = params ? new URLSearchParams(params).toString() : '';
    const response = await api.get(`/api/advances${qs ? `?${qs}` : ''}`);
    if (hasPaginationParams(params)) return response;
    return normalizeArrayPayload(response);
  },
  /** Full advance list for exports — no page/limit; pages through if backend still paginates. */
  getAllForExport: async (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      )
    ).toString();
    const first = await api.get(`/api/advances${qs ? `?${qs}` : ''}`);
    if (Array.isArray(first)) return first;

    const firstItems = Array.isArray(first?.items)
      ? first.items
      : Array.isArray(first?.data)
        ? first.data
        : [];
    const totalPages = Number(first?.pages) || 1;
    const total = Number(first?.total) || firstItems.length;

    if (totalPages <= 1 && firstItems.length >= total) {
      return firstItems;
    }

    const all = [...firstItems];
    const pageSize = Math.max(firstItems.length, 1);
    for (let page = 2; page <= totalPages; page += 1) {
      const pageParams = new URLSearchParams({
        ...Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
        ),
        page: String(page),
        limit: String(pageSize),
      }).toString();
      const next = await api.get(`/api/advances?${pageParams}`);
      const batch = Array.isArray(next)
        ? next
        : Array.isArray(next?.items)
          ? next.items
          : Array.isArray(next?.data)
            ? next.data
            : [];
      if (batch.length === 0) break;
      all.push(...batch);
    }
    return all;
  },
  getByEmployee: (empId) => api.get(`/api/advances/employee/${empId}`),
  getEmployeeSummary: (empId) => api.get(`/api/advances/employee/${empId}/summary`),
  getById: (id) => api.get(`/api/advances/${id}`),
  getDetails: (id) => api.get(`/api/advances/${id}/details`),
  create: (data) => api.post('/api/advances', data),
  update: (id, data) => api.put(`/api/advances/${id}`, data),
  updateStatus: (id, data) => api.put(`/api/advances/${id}/status`, data),
  cancel: (id) => api.put(`/api/advances/${id}/cancel`, {}),
  export: () => downloadBlob('/api/advances/export'),
};

// Audit & Notifications
export const auditApi = {
  getAll: async (params) => {
    const qs = params ? new URLSearchParams(params).toString() : '';
    const response = await api.get(`/api/audit-logs?${qs}`);
    if (hasPaginationParams(params)) return response;
    return normalizeArrayPayload(response);
  },
};

export const notificationApi = {
  getAll: () => api.get('/api/notifications'),
  markRead: (id) => api.put(`/api/notifications/${id}/read`, {}),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/api/dashboard/stats'),
};

// User management
export const userApi = {
  create: (data) => api.post('/api/users', data),
  reset: (employeeId, data) => api.put(`/api/users/${employeeId}/reset`, data),
  check: (employeeId) => api.get(`/api/users/${employeeId}`),
};

// Policy Documents
export const policyDocumentApi = {
  getAll: async (params) => {
    const qs = params ? new URLSearchParams(params).toString() : '';
    const response = await api.get(`/api/policy-documents${qs ? `?${qs}` : ''}`);
    if (hasPaginationParams(params)) return response;
    return normalizeArrayPayload(response);
  },
  getById: (id) => api.get(`/api/policy-documents/${id}`),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.postForm('/api/policy-documents/upload', formData);
  },
  create: (data) => api.post('/api/policy-documents', data),
  update: (id, data) => api.put(`/api/policy-documents/${id}`, data),
  remove: (id) => api.delete(`/api/policy-documents/${id}`),
  getSignedUrl: (id) => api.get(`/api/policy-documents/${id}/signed-url`),
  download: (id) => downloadBlob(`/api/policy-documents/${id}/download`),
};
